import { useState, useMemo } from "react";
import { Modal, Input, InputNumber, Select, DatePicker, Switch, Tooltip, Skeleton, Empty } from "antd";
import Swal from "sweetalert2";
import { toApiDate, fromApiDate, escapeHtml } from "./launchDates";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  RocketOutlined, SearchOutlined, CheckCircleOutlined, StopOutlined,
} from "@ant-design/icons";
import { isSuperAdmin } from "../utils/role";
import {
  useFeatureLaunches, useCreateFeatureLaunch,
  useUpdateFeatureLaunch, useToggleFeatureLaunch, useDeleteFeatureLaunch,
} from "../hooks/useFeatureLaunches";

/**
 * Feature launches (2026-10-26) — announce a newly launched feature on the user dashboard.
 *
 * Each live launch is a card in the dashboard's "What's new" drawer, in this screen's order
 * (Order, highest first; the first card is the big one). Everything the card shows comes from
 * here (2026-10-30): title, short text, the chip (Badge: Live / New / none) and the button text
 * (empty = "Try it"); the card opens the Link. There is no per-plan or credit button, so the
 * screen does not ask for a plan feature or a credit action (those columns stay, saved empty).
 * A launch is live while it is Active and inside its live window.
 */

// The live window is UTC in the API and local on screen (2026-10-27) — see launchDates.js.
const SHOW = (d) => fromApiDate(d).format("DD MMM YYYY");

const windowLabel = (from, to) => {
  if (!from && !to) return "Always";
  if (from && !to) return `From ${SHOW(from)}`;
  if (!from && to) return `Until ${SHOW(to)}`;
  return `${SHOW(from)} – ${SHOW(to)}`;
};

const thStyle = {
  padding: "14px 16px", textAlign: "left", fontSize: 12, fontWeight: 700,
  color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};
const tdStyle = { padding: "14px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const label = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };
const hint = { fontSize: 11.5, color: "#6b7280", marginTop: 5, lineHeight: 1.5 };
const errorText = { color: "#dc2626", fontSize: 12, marginTop: 5 };

const EMPTY_FORM = {
  title: "", short_text: "", link_url: "", whats_new_id: "", badge: null, cta_label: "",
  active_from: null, active_to: null, status: 1, sort_order: 0,
};
const blankToNull = (v) => (v == null || String(v).trim() === "" ? null : String(v).trim());

/* ── Add / Edit modal ─────────────────────────────────────────────────────── */
const LaunchFormModal = ({ launch, onClose }) => {
  const isEdit = Boolean(launch?.id);
  const [form, setForm] = useState(() => (isEdit ? {
    ...EMPTY_FORM,
    ...Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, launch[k] ?? EMPTY_FORM[k]])),
    active_from: fromApiDate(launch.active_from),
    active_to: fromApiDate(launch.active_to),
  } : { ...EMPTY_FORM }));
  const [errors, setErrors] = useState({});
  const set = (k) => (v) => { setForm((p) => ({ ...p, [k]: v })); setErrors((p) => ({ ...p, [k]: "" })); };

  const createMutation = useCreateFeatureLaunch();
  const updateMutation = useUpdateFeatureLaunch();
  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    const next = {};
    if (!form.title.trim()) next.title = "Title is required";
    const link = form.link_url.trim();
    // A dashboard path only — never an external or protocol-relative URL (the API checks too).
    if (!link.startsWith("/") || link.startsWith("//")) next.link_url = "Link must be a dashboard path starting with /";
    if (form.active_from && form.active_to && form.active_to.isBefore(form.active_from)) next.active_to = "Live until must be after live from";
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload = {
      title: form.title.trim(),
      short_text: blankToNull(form.short_text),
      link_url: link,
      whats_new_id: blankToNull(form.whats_new_id),
      badge: form.badge || null,
      cta_label: blankToNull(form.cta_label),
      active_from: toApiDate(form.active_from),
      active_to: toApiDate(form.active_to),
      status: form.status,
      sort_order: Number(form.sort_order) || 0,
    };
    const mutation = isEdit ? updateMutation : createMutation;
    mutation.mutate(isEdit ? { id: launch.id, ...payload } : payload, { onSuccess: onClose });
  };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>{isEdit ? "Edit launch" : "Add launch"}</span>}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Save"}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={560}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={label}>Title <span style={{ color: "#dc2626" }}>*</span></label>
          <Input value={form.title} onChange={(e) => set("title")(e.target.value)} maxLength={120}
            placeholder="e.g. AI Opinion is live" status={errors.title ? "error" : ""} />
          {errors.title && <div style={errorText}>{errors.title}</div>}
        </div>

        <div>
          <label style={label}>Short text</label>
          <Input.TextArea value={form.short_text} onChange={(e) => set("short_text")(e.target.value)} rows={2} maxLength={255}
            placeholder="One line shown under the title in What's new (optional)" />
        </div>

        <div>
          <label style={label}>Link <span style={{ color: "#dc2626" }}>*</span></label>
          <Input value={form.link_url} onChange={(e) => set("link_url")(e.target.value)} maxLength={255}
            placeholder="/dashboard/…" status={errors.link_url ? "error" : ""} />
          {errors.link_url ? <div style={errorText}>{errors.link_url}</div>
            : <div style={hint}>The dashboard page the card opens.</div>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={label}>Badge</label>
            <Select value={form.badge} onChange={set("badge")} allowClear placeholder="None" style={{ width: "100%" }}
              options={[{ value: "Live", label: "Live" }, { value: "New", label: "New" }]} />
            <div style={hint}>The chip beside the title in What's new.</div>
          </div>
          <div>
            <label style={label}>Button text</label>
            <Input value={form.cta_label} onChange={(e) => set("cta_label")(e.target.value)} maxLength={60} placeholder="Try it" />
            <div style={hint}>e.g. "Open Notice Inbox". Empty shows "Try it".</div>
          </div>
        </div>

        <div>
          <label style={label}>What's New id</label>
          <Input value={form.whats_new_id} onChange={(e) => set("whats_new_id")(e.target.value)} maxLength={80} placeholder="e.g. ai-opinion" />
          <div style={hint}>"Learn more" opens /whats-new#&lt;id&gt;. Leave empty to hide it.</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={label}>Live from</label>
            <DatePicker showTime value={form.active_from} onChange={set("active_from")} style={{ width: "100%" }} placeholder="Now" />
          </div>
          <div>
            <label style={label}>Live until</label>
            <DatePicker showTime value={form.active_to} onChange={set("active_to")} style={{ width: "100%" }} placeholder="No end"
              status={errors.active_to ? "error" : ""} />
            {errors.active_to && <div style={errorText}>{errors.active_to}</div>}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div>
            <label style={label}>Order</label>
            <InputNumber value={form.sort_order} onChange={(v) => set("sort_order")(v ?? 0)} precision={0} />
            <div style={hint}>Higher shows first.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Switch checked={form.status === 1} onChange={(checked) => set("status")(checked ? 1 : 0)} />
            <span style={{ fontSize: 13, fontWeight: 600, color: form.status === 1 ? "#16a34a" : "#6b7280" }}>
              {form.status === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/* ── Page ─────────────────────────────────────────────────────────────────── */
const ManageFeatureLaunches = () => {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);   // launch object, or {} to add

  const { data: launches = [], isLoading, refetch, isFetching } = useFeatureLaunches();
  const toggleMutation = useToggleFeatureLaunch();
  const deleteMutation = useDeleteFeatureLaunch();
  const mayManage = isSuperAdmin();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return launches;
    return launches.filter((l) => l.title?.toLowerCase().includes(q) || l.link_url?.toLowerCase().includes(q));
  }, [launches, search]);

  const handleToggle = (launch) => {
    const isActive = launch.status === 1;
    Swal.fire({
      title: isActive ? "Hide this launch?" : "Show this launch?",
      html: isActive
        ? `<b>${escapeHtml(launch.title)}</b> will stop showing on the dashboard.`
        : `<b>${escapeHtml(launch.title)}</b> will show on the dashboard while it is inside its live window.`,
      icon: isActive ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor: isActive ? "#dc2626" : "#16a34a",
      cancelButtonColor: "#6b7280",
      confirmButtonText: isActive ? "Yes, hide" : "Yes, show",
      reverseButtons: true,
    }).then((r) => { if (r.isConfirmed) toggleMutation.mutate(launch.id); });
  };

  const handleDelete = (launch) => {
    Swal.fire({
      title: `Delete "${launch.title}"?`,
      html: "It disappears from every user's dashboard.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    }).then((r) => { if (r.isConfirmed) deleteMutation.mutate(launch.id); });
  };

  const iconBtn = (color, border, bg) => ({
    width: 32, height: 32, borderRadius: 8, border: `1px solid ${border}`,
    background: bg, color, cursor: "pointer", display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 13,
  });

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "28px 32px", marginBottom: 28, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, color: "white" }}>
                <RocketOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>Feature launches</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>Announce new features on the user dashboard</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Tooltip title="Refresh">
                <button onClick={() => refetch()} style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              {mayManage && (
                <button onClick={() => setEditing({})} style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 20px", borderRadius: 10, border: "none", background: "white", color: "#5b48e8", fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                  <PlusOutlined /> Add Launch
                </button>
              )}
            </div>
          </div>

          {/* Card */}
          <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f0f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title or link…"
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} allowClear style={{ maxWidth: 340 }} />
              <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
                {filtered.length} {filtered.length === 1 ? "launch" : "launches"}
              </span>
            </div>

            {isLoading ? (
              <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 5 }} /></div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "48px 20px" }}>
                <Empty description={search ? "No launch matches this search." : "No launches yet — add your first one."} />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#fafbff" }}>
                    <tr>
                      <th style={thStyle}>Title</th>
                      <th style={thStyle}>Link</th>
                      <th style={thStyle}>Badge</th>
                      <th style={thStyle}>Button</th>
                      <th style={thStyle}>Live window</th>
                      <th style={thStyle}>Order</th>
                      <th style={thStyle}>Status</th>
                      {mayManage && <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => {
                      const isActive = l.status === 1;
                      return (
                        <tr key={l.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={{ ...tdStyle, fontWeight: 700, color: "#111827" }}>{l.title}</td>
                          <td style={tdStyle}>
                            <span style={{ fontFamily: "monospace", fontSize: 12, background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap" }}>
                              {l.link_url}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            {l.badge ? (
                              <span style={{ background: "#2563eb", color: "white", borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{l.badge}</span>
                            ) : <span style={{ color: "#9ca3af" }}>—</span>}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: "nowrap", color: "#2563eb", fontWeight: 600 }}>{`${l.cta_label || "Try it"} →`}</td>
                          <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{windowLabel(l.active_from, l.active_to)}</td>
                          <td style={{ ...tdStyle, color: "#6b7280" }}>{l.sort_order ?? 0}</td>
                          <td style={tdStyle}>
                            <span style={{ background: isActive ? "#f0fdf4" : "#f5f5f5", color: isActive ? "#16a34a" : "#6b7280", border: `1px solid ${isActive ? "#bbf7d0" : "#e5e7eb"}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          {mayManage && (
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <Tooltip title="Edit">
                                  <button onClick={() => setEditing(l)} style={iconBtn("#5b48e8", "#ddd6fe", "#f5f3ff")}><EditOutlined /></button>
                                </Tooltip>
                                <Tooltip title={isActive ? "Hide" : "Show"}>
                                  <button onClick={() => handleToggle(l)} disabled={toggleMutation.isPending}
                                    style={iconBtn(isActive ? "#b45309" : "#16a34a", isActive ? "#fde68a" : "#bbf7d0", isActive ? "#fffbeb" : "#f0fdf4")}>
                                    {isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                                  </button>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <button onClick={() => handleDelete(l)} disabled={deleteMutation.isPending} style={iconBtn("#dc2626", "#fecaca", "#fef2f2")}>
                                    <DeleteOutlined />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && <LaunchFormModal launch={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default ManageFeatureLaunches;
